/**
 * LibrairieTemplate — "Literary, timeless, smelling of ink and old paper"
 * Design spec: T10 from design_guidelines.json
 * Palette: warm paper (#F4F1EA), ink (#2C363F), blood-red accent (#8C271E)
 * Fonts: Spectral (display) + Crimson Text (body) — serif editorial pairing
 */
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string;
  desc: string;
  product: string;
  city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const c = (f: keyof WebsiteCustomizations, fallback: string, customizations?: WebsiteCustomizations) =>
  customizations?.[f] || fallback;

const LibrairieTemplate = ({ name, desc, product, city, customizations, editMode, onCustomize }: Props) => {
  const heroTitle = c("heroTitle", name || "La Librairie du Temps", customizations);
  const heroSub = c("heroSubtitle", desc || "Une sélection intemporelle de lectures pour les esprits curieux.", customizations);
  const cta = c("ctaText", "Découvrir la sélection", customizations);
  const phone = c("phone", "01 45 67 89 12", customizations);
  const email = c("email", `contact@${(name || "librairie").toLowerCase().replace(/\s/g, "")}.fr`, customizations);
  const set = (f: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(f, v);

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "#F4F1EA",
        color: "#2C363F",
        fontFamily: "'Crimson Text', Georgia, serif",
      }}
      data-testid="template-librairie"
    >
      {/* Subtle paper grain */}
      <div
        className="fixed inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Top bar — masthead style */}
      <header className="border-b border-[#2C363F]/20 py-4 px-6 md:px-12 flex items-center justify-between relative">
        <div className="text-xs tracking-[0.3em] uppercase opacity-60">Depuis 1987</div>
        <EditableText
          as="div"
          value={name || "Grafton Éditions"}
          onChange={set("logoText")}
          editMode={editMode}
          data-field="logoText"
          className="font-bold tracking-wide"
          style={{ fontFamily: "'Spectral', serif", fontSize: "22px" }}
        />
        <div className="text-xs tracking-[0.3em] uppercase opacity-60 hidden md:block">{city || "Paris"}</div>
      </header>

      {/* Hero — Broadsheet editorial */}
      <section className="py-16 md:py-24 px-6 md:px-12 relative">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7 border-r-0 md:border-r md:border-[#2C363F]/15 md:pr-10">
              <div className="mb-6">
                <span className="inline-block w-16 h-px bg-[#8C271E]" />
                <span className="ml-3 text-xs tracking-[0.3em] uppercase" style={{ color: "#8C271E" }}>
                  Édition du mois
                </span>
              </div>
              <EditableText
                as="h1"
                value={heroTitle}
                onChange={set("heroTitle")}
                editMode={editMode}
                data-field="heroTitle"
                className="leading-[0.95] mb-8"
                style={{
                  fontFamily: "'Spectral', serif",
                  fontSize: "clamp(2.8rem, 6vw, 5.5rem)",
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                }}
              />
              <div
                className="text-lg md:text-xl leading-[1.75]"
                style={{ columnCount: 1 }}
              >
                <span
                  className="float-left mr-3 leading-[0.85]"
                  style={{
                    fontFamily: "'Spectral', serif",
                    fontSize: "5.5em",
                    fontWeight: 700,
                    color: "#8C271E",
                    lineHeight: 0.85,
                    marginTop: "0.05em",
                  }}
                >
                  {heroSub.charAt(0)}
                </span>
                <EditableText
                  as="span"
                  value={heroSub.slice(1)}
                  onChange={(v) => set("heroSubtitle")(heroSub.charAt(0) + v)}
                  editMode={editMode}
                  data-field="heroSubtitle"
                  multiline
                  style={{ fontStyle: "italic" }}
                />
              </div>
              <div className="mt-10 flex items-center gap-4 flex-wrap">
                <EditableText
                  as="button"
                  value={cta}
                  onChange={set("ctaText")}
                  editMode={editMode}
                  data-field="ctaText"
                  className="px-7 py-3 border-2 transition-all hover:bg-[#2C363F] hover:text-[#F4F1EA]"
                  style={{
                    borderColor: "#2C363F",
                    color: "#2C363F",
                    fontFamily: "'Spectral', serif",
                    fontStyle: "italic",
                    fontSize: "17px",
                    fontWeight: 600,
                  }}
                />
                <div className="text-sm opacity-60 italic">ou prenez le temps de flâner →</div>
              </div>
            </div>

            <aside className="md:col-span-5 md:sticky md:top-8">
              <div className="relative aspect-[3/4] rounded-sm overflow-hidden shadow-[8px_8px_0_rgba(140,39,30,0.1)]">
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(135deg, #E6DFD1 0%, #C9BFAB 100%)",
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div
                      className="mb-4"
                      style={{
                        fontFamily: "'Spectral', serif",
                        fontSize: "11px",
                        letterSpacing: "0.4em",
                        textTransform: "uppercase",
                        color: "#8C271E",
                      }}
                    >
                      — Le Livre du Mois —
                    </div>
                    <div
                      style={{
                        fontFamily: "'Spectral', serif",
                        fontSize: "38px",
                        fontWeight: 700,
                        lineHeight: 1.1,
                        color: "#2C363F",
                      }}
                    >
                      L'Art de la<br />
                      <em style={{ color: "#8C271E" }}>Lenteur</em>
                    </div>
                    <div className="mt-6 text-sm italic opacity-70">par Sylvain Tesson</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-xs italic opacity-60 px-2">
                « Un éloge de la rareté dans un monde saturé. »
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* New arrivals grid */}
      <section className="py-16 px-6 md:px-12 border-t border-[#2C363F]/15">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline justify-between mb-10 border-b border-[#2C363F]/20 pb-4">
            <h2 style={{ fontFamily: "'Spectral', serif", fontSize: "38px", fontWeight: 600, letterSpacing: "-0.01em" }}>
              Nouveautés
            </h2>
            <span className="text-xs tracking-[0.3em] uppercase opacity-60">Printemps 2026</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { t: "Littérature", e: "La Rivière", a: "Marie Nimier" },
              { t: "Essai", e: "Silences", a: "François Jullien" },
              { t: "Poésie", e: "Embrasure", a: "Esther Tellermann" },
              { t: "Sciences", e: "Biosphère", a: "Bruno Latour" },
            ].map((b, i) => (
              <div key={i} className="group cursor-pointer">
                <div
                  className="aspect-[2/3] mb-4 relative overflow-hidden transition-transform duration-500 group-hover:-translate-y-1"
                  style={{ background: i % 2 === 0 ? "#E6DFD1" : "#2C363F" }}
                >
                  <div className="absolute inset-0 flex flex-col justify-between p-4">
                    <div
                      className="text-[10px] tracking-[0.25em] uppercase"
                      style={{ color: i % 2 === 0 ? "#8C271E" : "#E6DFD1" }}
                    >
                      {b.t}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Spectral', serif",
                        fontSize: "22px",
                        fontWeight: 700,
                        color: i % 2 === 0 ? "#2C363F" : "#F4F1EA",
                        lineHeight: 1.1,
                      }}
                    >
                      {b.e}
                    </div>
                  </div>
                </div>
                <div className="text-sm italic">{b.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Author spotlight split */}
      <section className="py-20 px-6 md:px-12 border-t border-[#2C363F]/15" style={{ background: "#E6DFD1" }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-3">
            <span className="text-xs tracking-[0.3em] uppercase" style={{ color: "#8C271E" }}>
              Rencontre
            </span>
            <h2
              className="mt-3 mb-6"
              style={{ fontFamily: "'Spectral', serif", fontSize: "46px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.05 }}
            >
              <em>Chaque mois,</em><br />
              un auteur, <br />
              une conversation.
            </h2>
            <EditableText
              as="p"
              value={c("serviceTitle", "Un jeudi sur deux, 19h30. Dans la salle du fond, parmi les livres, nous accueillons un écrivain pour un échange sans filtre.", customizations)}
              onChange={set("serviceTitle")}
              editMode={editMode}
              data-field="serviceTitle"
              className="text-lg leading-relaxed opacity-85"
              style={{ fontStyle: "italic" }}
              multiline
            />
            <div className="mt-8 flex items-center gap-4">
              <span className="w-16 h-px bg-[#2C363F]/40" />
              <span className="text-xs tracking-[0.25em] uppercase opacity-70">Prochaine date : 14 mai</span>
            </div>
          </div>
          <div className="md:col-span-2 aspect-[4/5] relative" style={{ background: "#2C363F" }}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                style={{
                  fontFamily: "'Spectral', serif",
                  fontSize: "140px",
                  color: "#8C271E",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                “
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14 px-6 md:px-12 border-t border-[#2C363F]/20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <div style={{ fontFamily: "'Spectral', serif", fontSize: "24px", fontWeight: 700 }}>
              {name || "Grafton Éditions"}
            </div>
            <p className="text-sm italic mt-2 opacity-70">
              {c("footerDesc", "Une maison, un catalogue, une idée du livre.", customizations)}
            </p>
          </div>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase opacity-60 mb-3">Adresse</div>
            <div className="text-sm italic">{city || "Paris"}, 12 rue des Écoles, 75005</div>
            <div className="text-sm italic mt-1">Ouvert du mardi au samedi · 10h—19h</div>
          </div>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase opacity-60 mb-3">Contact</div>
            <EditableText as="div" value={phone} onChange={set("phone")} editMode={editMode} data-field="phone" className="text-sm italic" />
            <EditableText as="div" value={email} onChange={set("email")} editMode={editMode} data-field="email" className="text-sm italic mt-1" />
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-12 pt-6 border-t border-[#2C363F]/15 text-center text-xs italic opacity-50">
          Lire, c'est encore la meilleure chose à faire de son temps libre. — {product || name}
        </div>
      </footer>
    </div>
  );
};

export default LibrairieTemplate;
